import {
    bool2str,
    componentId,
    setAttributes,
    migrateChildNodes,
} from '../../../../FrontendSrc/JavaScript/util';
import { MultiPanelElement } from '../multi-panel-element';

const ACCORDION_CLASS_TITLE = 'js-accordion__title';
const ACCORDION_CLASS_CONTENT = 'js-accordion__content';
const ACCORDION_CLASS_BUTTON = '__button';
const ACCORDION_CLASS_INIT = 'js-accordion--init';

const getPanelId = r => `accordion${r}_panel`;
const getButtonId = r => `accordion${r}_button`;

/**
 * Takes a structure like
 * ```html
 * div.js-accordion
 *   h3.accordion__title(data-selected="true")
 *   div.js-accordion__content
 *     //- arbitrary content
 *   h3.accordion__title(data-selected="true")
 *   div.js-accordion__content
 * ```
 * and produces
 * ```html
 * div.js-accordion
 *   div.accordion__panel
 *     h3.js-accordion__title(data-selected="true")
 *       button.<baseclass>__button
 *     div.js-accordion__content
 *       //- arbitrary content
 *   div.accordion__panel
 *     // ...
 * ```
 *
 * ## API
 * ```
 * const a = new Accordion(document.querySelector('.js-accordion'), 'accordion')
 * a.activePanel // div.js-accordion__content || null
 * a.activePanel = document.querySelectorAll('.js-accordion__content')[1]
 * // or
 * a.openPanel(document.querySelectorAll('.js-accordion__content')[1], true|false)
 * a.multiselect // div.js-accordion[data-accordion-multiselect=true]
 * a.forceOpen // div.js-accordion[data-accordion-force-open=true]
 * a.wrapFocus // div.js-accordion[data-accordion-wrap-focus=true]
 * a.destroy() // revert changes made by this component
 * ```
 *
 * ## Keyboard-interaction
 * on buttons
 * * UP: focus previous button
 * * DOWN: focus next button
 * * HOME: focus first button
 * * END: focus last button
 */
class Accordion extends MultiPanelElement {

    /**
     * @param {HTMLDivElement} element
     * @param {string} baseclass
     */
    constructor(element, baseclass = 'accordion') {
        super(element);
        this.destroy = this.destroy.bind(this);
        this.openPanel = this.openPanel.bind(this);
        this._construct = this._construct.bind(this);
        this._openPanel = this._openPanel.bind(this);
        this._onButtonClick = this._onButtonClick.bind(this);
        this._onButtonKey = this._onButtonKey.bind(this);
        /**
         * @type {HTMLDivElement}
         * @private
         */
        this._element = element;
        this._baseclass = baseclass;
        /**
         * @type {HTMLButtonElement[]}
         * @private
         */
        this._buttons = [];
        this._construct();
    }

    static get elementProperty() {
        return '_accordion';
    }

    /**
     * When enabled, at least one panel has to remain open, i.e. the last open
     * panel can't be closed
     * @return {boolean}
     */
    get forceOpen() {
        return this._element.dataset.accordionForceOpen === 'true';
    }

    /**
     * Set whether at least one panel has to remain open
     * @param {boolean} value
     */
    set forceOpen(value) {
        if (value === true) {
            this._element.dataset.accordionForceOpen = 'true';
        } else {
            delete this._element.dataset.accordionForceOpen;
        }
    }

    /**
     * When enabled, multiple panels can be expanded at a given time. Otherwise,
     * opening a panel will close other possibly open panels.
     * @return {boolean}
     */
    get multiselect() {
        return this._element.dataset.accordionMultiselect === 'true';
    }

    /**
     * Set whether multiple panels can be expanded at the same time.
     * @param {boolean} value
     */
    set multiselect(value) {
        if (value === true) {
            this._element.dataset.accordionMultiselect = 'true';
        } else {
            delete this._element.dataset.accordionMultiselect;
        }
    }

    /**
     * When enabled, focusing the next/previous panel header with the arrow keys
     * can wrap to the first/last panel header respectively.
     * @return {boolean}
     */
    get wrapFocus() {
        return this._element.dataset.accordionWrapFocus === 'true';
    }

    /**
     * Set whether arrow-key focus can wrap to the first/last panel header.
     * @param {boolean} value
     */
    set wrapFocus(value) {
        if (value === true) {
            this._element.dataset.accordionWrapFocus = 'true';
        } else {
            delete this._element.dataset.accordionWrapFocus;
        }
    }

    /**
     * When enabled, the first panel will be expanded when constructing the accordion.
     * @return {boolean}
     */
    get expandFirstPanel() {
        return this._element.dataset.accordionExpandFirst === 'true';
    }

    /**
     * Set whether first panel will be expanded when constructing the accordion.
     * @param {boolean} value
     */
    set expandFirstPanel(value) {
        if (value === true) {
            this._element.dataset.accordionExpandFirst = 'true';
        } else {
            delete this._element.dataset.accordionExpandFirst;
        }
    }

    get panels() {
        return Array.from(this._element.querySelectorAll('[role=region]'));
    }

    /**
     * The currently active panel element will usually be the title's next
     * element sibling and have the role "region" (i.e. the content container,
     * not the panel container containing the title)
     * @returns {Element}
     */
    get activePanel() {
        return this._element.querySelector('[role=region][aria-hidden=false]');
    }

    set activePanel(panel) {
        this.openPanel(panel, true);
    }

    openPanel(panel, targetState) {
        if (!this._element.contains(panel)) {
            throw new ReferenceError('Given element is not part of this Accordion');
        }
        this._openPanel(panel, targetState);
    }

    /**
     * Create DOM structure and attach attributes and events
     * @private
     */
    _construct() {
        super._construct();
        let tabOpened = false;
        let tabSelected = false;
        const controlIds = [];
        (/** @type {HTMLHeadingElement[]} */(Array.from(this._element.querySelectorAll('.' + ACCORDION_CLASS_TITLE)))).forEach((title, i) => {
            let panel = title.nextElementSibling;
            const isPanel = p => p.classList && p.classList.contains(ACCORDION_CLASS_CONTENT);
            while (!isPanel(panel)) {
                panel = panel.nextElementSibling;
            }
            if (panel === null || !isPanel(panel)) {
                throw new ReferenceError(`Accordion element ${i + 1} has no adjacent content`);
            }
            const isSelected = title.dataset.selected === 'true' && (!tabOpened || this.multiselect);
            tabOpened = tabOpened || isSelected;
            const randId = componentId();
            const panelId = getPanelId(randId);
            const buttonId = getButtonId(randId);
            const button = document.createElement('BUTTON');
            this._buttons.push(button);
            migrateChildNodes(title, button);
            button.classList.add(this._baseclass + ACCORDION_CLASS_BUTTON);
            button.addEventListener('click', this._onButtonClick);
            button.addEventListener('keydown', this._onButtonKey);
            setAttributes(button, {
                'aria-controls': panelId,
                'aria-expanded': isSelected,
                'id': buttonId,
                'type': 'button',
            });
            title.appendChild(button);
            setAttributes(panel, {
                'aria-labelledby': buttonId,
                'aria-hidden': !isSelected,
                'role': 'region',
                'id': panelId,
            });
            controlIds.push(buttonId);
            if (!tabSelected && isSelected) {
                button.dataset.selected = 'true';
                tabSelected = true;
            }
        });
        if (!tabSelected && this._buttons.length > 0) {
            if (this.forceOpen && !tabOpened) {
                const button = this._buttons[0];
                const title = button.parentElement;
                const panel = title.nextElementSibling;
                button.setAttribute('aria-expanded', 'true');
                title.setAttribute('data-selected', 'true');
                panel.setAttribute('aria-hidden', 'false');
            }
        }
        if (this.expandFirstPanel) {
            this.openPanel(this.panels[0], true);
        }
        this._element.classList.add(ACCORDION_CLASS_INIT);
        this._onHashChange();
    }

    _openPanel(panel, targetState) {
        const activePanel = this.activePanel;
        if (panel === activePanel && targetState) {
            return;
        }
        if (!this.multiselect && targetState) {
            Array.from(this._element.querySelectorAll('[role=region][aria-hidden=false]'))
                .forEach(e => this._openPanel(e, false));
        }
        const button = panel.previousElementSibling.firstChild;
        const title = panel.previousElementSibling;
        button.setAttribute('aria-expanded', bool2str(targetState));
        title.setAttribute('data-selected', bool2str(targetState));
        panel.setAttribute('aria-hidden', bool2str(!targetState));
        if (!this.multiselect && targetState) {
            // when the title is off-screen due to a collapsing panel above, we should focus it
            if (title.getBoundingClientRect().top < 0) {
                title.scrollIntoView();
            }
        }
    }

    _focusButton(currentButton, nextIndex) {
        this._buttons[nextIndex].focus();
    }

    _focusNextButton(currentIndex) {
        if (currentIndex === this._buttons.length - 1 && !this.wrapFocus) {
            return;
        }

        this._focusButton(
            this._buttons[currentIndex],
            (currentIndex + 1) % this._buttons.length
        );
    }

    _focusPreviousButton(currentIndex) {
        if (currentIndex === 0 && !this.wrapFocus) {
            return;
        }

        this._focusButton(
            this._buttons[currentIndex],
            (currentIndex - 1 + this._buttons.length) % this._buttons.length
        );
    }

    /**
     * @param {MouseEvent} e
     * @private
     */
    _onButtonClick(e) {
        e.preventDefault();
        const button = e.currentTarget;
        // id of the panel
        const panel = this._element.querySelector('#' + button.getAttribute('aria-controls'));
        setTimeout(() => button.focus(), 0);
        this._openPanel(panel, button.getAttribute('aria-expanded') !== 'true');
    }

    /**
     * @param {KeyboardEvent} e
     * @private
     */
    _onButtonKey(e) {
        const button = e.target;
        let currentTabIndex = this._buttons.indexOf(button);
        const buttonCount = this._buttons.length;
        if (!e.ctrlKey) {
            switch (e.key) {
                // previous tab
                case 'ArrowUp':
                    e.preventDefault();
                    this._focusPreviousButton(currentTabIndex);
                    break;
                // next tab
                case 'ArrowDown':
                    e.preventDefault();
                    this._focusNextButton(currentTabIndex);
                    break;
                // first tab
                case 'Home':
                    e.preventDefault();
                    this._focusButton(button, 0);
                    break;
                // last tab
                case 'End':
                    e.preventDefault();
                    this._focusButton(button, buttonCount - 1);
                    break;
            }
        }
    }

    destroy() {
        super.destroy();
        this._buttons.forEach(button => {
            const panel = button.parentNode.nextElementSibling;
            const title = button.parentNode;
            migrateChildNodes(button, title);
            setAttributes(panel, {
                'aria-labelledby': null,
                'aria-hidden': null,
                'id': null,
            });
            button.remove();
        });
        this._element.classList.remove(ACCORDION_CLASS_INIT);
    }
}

export {
    Accordion,
};
